import css from '@styled-system/css';
import React from 'react';
import { useSelect } from 'downshift';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { border, color, compose, layout, margin, shadow, space } from 'styled-system';
import List from './List';
import ListItem from './ListItem';
import theme from '../theme';
import { transparentize } from 'polished';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
function boxStyles(props) {
    const boxSize = props.boxSize || 'medium';
    const boxTypes = {
        large: {
            padding: '3',
            fontSize: '1'
        },
        medium: {
            padding: '2',
            fontSize: '0'
        },
        small: {
            padding: '1',
            fontSize: '12px'
        }
    };
    return css(boxTypes[boxSize]);
}
function wrapperStyles(props) {
    const boxSize = props.boxSize || 'medium';
    const wrapperTypes = {
        large: {
            width: '250px'
        },
        medium: {
            width: '165px'
        },
        small: {
            width: '100px'
        }
    };
    return css(wrapperTypes[boxSize]);
}
function listItemStyles(props) {
    const boxSize = props.boxSize || 'medium';
    const textTypes = {
        large: {
            fontSize: '1',
            padding: '3'
        },
        medium: {
            fontSize: '0',
            padding: '2'
        },
        small: {
            fontSize: '12px',
            padding: '1'
        }
    };
    return css(textTypes[boxSize]);
}
const StyledWrapper = styled.div `
  ${css({
    display: 'block',
    position: 'relative'
})}
  ${wrapperStyles}
  ${compose(margin, layout)}
`;
function toggledButtonStyles(props) {
    if (!props.isOpen) {
        return;
    }
    return css({
        boxShadow: `0 0 1px 1px ${themeGet('colors.gray.2', 'lightgray')(props)}`,
        borderColor: 'gray.2',
        borderStyle: 'solid',
        borderWidth: '1',
        outline: '0'
    });
}
const StyledButton = styled.button `
  ${css({
    alignItems: 'center',
    bg: 'white',
    borderColor: 'gray.3',
    borderRadius: '1',
    borderStyle: 'solid',
    borderWidth: '1',
    color: 'gray.0',
    display: 'flex',
    flexDirection: 'row',
    fontFamily: 'body',
    fontWeight: 'light',
    px: '3',
    py: '2',
    width: '100%'
})}
  ${boxStyles}
  &:focus {
    ${props => toggledButtonStyles(Object.assign(Object.assign({}, props), { isOpen: true }))}
  }
  &:disabled {
    ${css({
    bg: 'gray.5',
    color: 'gray.3',
    cursor: 'not-allowed'
})}
  }
  ${toggledButtonStyles}
  ${compose(border, color, layout, space)}
`;
const StyledList = styled(List) `
  width: -moz-available;
  width: -webkit-fill-available;
  ${css({
    bg: 'white',
    borderColor: 'gray.4',
    borderRadius: '1',
    borderStyle: 'solid',
    borderWidth: '1',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    cursor: 'default',
    maxHeight: '180px',
    my: '1',
    overflow: 'auto',
    position: 'absolute'
})}
  ${({ zIndex }) => {
    const zIndexValue = zIndex || '8000';
    return css({
        zIndex: zIndexValue
    });
}}
  &:focus {
    ${css({
    outline: '0'
})}
  }
  ${compose(border, color, layout, shadow, space)}
`;
const StyledListItem = styled(ListItem) `
  ${({ theme, isSelected, isHighlighted, selectedColor }) => {
    let color = selectedColor || 'brand';
    color = themeGet(`colors.${color}`, color)({ theme });
    let bgColor = isHighlighted ? transparentize(0.75, color) : '';
    bgColor = isSelected ? color : bgColor;
    return css({
        bg: bgColor,
        color: isSelected ? 'white' : ''
    });
}}
  ${listItemStyles}
`;
const StyledLabel = styled.span `
  ${({ hasSelected }) => css({
    color: !hasSelected ? 'gray.3' : '',
    mr: '4'
})}
`;
const DownIcon = styled(FontAwesomeIcon) `
  ${({ disabled, selectedColor }) => css({
    color: !disabled ? selectedColor || 'brand' : 'gray.3',
    marginLeft: 'auto'
})}
`;
function Select({ options, defaultValue, placeholder, onChange, id, name, disabled, color, drop, button, wrapper, boxSize, theme, zIndex }) {
    const { isOpen, selectedItem, getToggleButtonProps, getMenuProps, highlightedIndex, getItemProps } = useSelect({
        defaultSelectedItem: defaultValue || null,
        items: options,
        itemToString: i => (!i || i.label == null ? '' : String(i.label)),
        onSelectedItemChange: changes => onChange &&
            changes &&
            changes.selectedItem &&
            onChange(changes.selectedItem)
    });
    const label = selectedItem
        ? selectedItem.label
        : defaultValue
            ? defaultValue.label
            : placeholder || 'Select...';
    return (React.createElement(StyledWrapper, Object.assign({ id: id, name: name, boxSize: boxSize, zIndex: zIndex }, wrapper),
        React.createElement(StyledButton, Object.assign({ isOpen: isOpen, disabled: disabled, boxSize: boxSize, theme: theme }, button, getToggleButtonProps()),
            React.createElement(StyledLabel, { hasSelected: Boolean(selectedItem) }, label),
            React.createElement(DownIcon, { icon: faChevronDown, selectedColor: color, disabled: disabled, "aria-hidden": 'true' })),
        isOpen ? (React.createElement(StyledList, Object.assign({ zIndex: zIndex }, getMenuProps(), drop), options.map((item, index) => (React.createElement(StyledListItem, Object.assign({ boxSize: boxSize, selectedColor: color, isSelected: selectedItem && selectedItem.value === item.value, isHighlighted: highlightedIndex === index, key: `${item.value}${index}` }, getItemProps({ item, index, key: item.value })), item.label))))) : null));
}
Select.defaultProps = { theme };
export default Select;
//# sourceMappingURL=Select.js.map